import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import {
    formatCurrency,
    formatDate,
    formatPrice,
    formatThousands,
} from "@/utils";

// Nilai retur per baris dibulatkan ke rupiah (harga jual bisa 2 desimal, mis. 333,33)
const getLineAmount = (item) =>
    Math.round((Number(item.quantity) || 0) * (Number(item.price) || 0));

const SalesOrderReturnPrint = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, isLoading, error } = useFetchData(
        `sales-order-returns/${id}/show`,
    );

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales/return/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
            </div>
        );
    }

    const returnData = data;
    const items = returnData?.items || [];

    const grandTotal = items.reduce(
        (sum, item) => sum + getLineAmount(item),
        0,
    );

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales/return/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Retur_${returnData?.return_number || "Penjualan"}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="RETUR PENJUALAN" />

                {/* Return Info */}
                <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                    <div className="flex">
                        <span className="w-32 font-medium">No. Retur</span>
                        <span>: {returnData?.return_number}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Tanggal</span>
                        <span>: {formatDate(returnData?.return_date)}</span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">No. Sales Order</span>
                        <span>
                            : {returnData?.returnable?.order_number || "-"}
                        </span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Customer</span>
                        <span>
                            :{" "}
                            {returnData?.returnable?.customer?.name || "-"}
                        </span>
                    </div>
                    <div className="flex">
                        <span className="w-32 font-medium">Status Approval</span>
                        <span>
                            : {returnData?.approval_status?.name || "-"}
                        </span>
                    </div>
                </div>

                {/* Items Table */}
                <table className="mb-4 w-full border-collapse border border-black text-xs">
                    <thead>
                        <tr className="bg-default-100">
                            <th className="border border-black p-1.5 text-center">
                                No.
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                Barang
                            </th>
                            <th className="border border-black p-1.5 text-center">
                                Satuan
                            </th>
                            <th className="border border-black p-1.5 text-center">
                                Gudang
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Jumlah
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Harga
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Subtotal
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.id}>
                                <td className="border border-black p-1.5 text-center">
                                    {index + 1}.
                                </td>
                                <td className="border border-black p-1.5">
                                    {item.material?.name || "-"}
                                </td>
                                <td className="border border-black p-1.5 text-center">
                                    {item.unit?.name || "-"}
                                </td>
                                <td className="border border-black p-1.5 text-center">
                                    {item.warehouse?.name || "-"}
                                </td>
                                <td className="border border-black p-1.5 text-right">
                                    {formatThousands(item.quantity)}
                                </td>
                                <td className="border border-black p-1.5 text-right">
                                    {formatPrice(item.price)}
                                </td>
                                <td className="border border-black p-1.5 text-right">
                                    {formatCurrency(getLineAmount(item))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-default-100 font-bold">
                            <td
                                colSpan={6}
                                className="border border-black p-1.5 text-center"
                            >
                                TOTAL
                            </td>
                            <td className="border border-black p-1.5 text-right">
                                {formatCurrency(grandTotal)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* Keterangan */}
                {returnData?.description && (
                    <div className="mb-6 text-xs">
                        <span className="font-medium">Keterangan: </span>
                        {returnData.description}
                    </div>
                )}

                {/* Signature area */}
                <div className="mt-12 grid grid-cols-2 gap-8 text-xs">
                    <div className="text-center">
                        <p className="font-medium">Penjual</p>
                        <div className="mt-16">
                            (.................................)
                        </div>
                    </div>
                    <div className="text-center">
                        <p className="font-medium">Customer</p>
                        <div className="mt-16">
                            (.................................)
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-8 space-y-3 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                    <div className="flex flex-col gap-1">
                        <p className="font-medium">Syarat & Ketentuan:</p>
                        <ol className="list-disc space-y-0.5 pl-4">
                            <li>
                                Retur hanya dapat dilakukan atas barang yang
                                telah terbukti rusak atau tidak sesuai pesanan.
                            </li>
                            <li>
                                Pengajuan retur harus disertai bukti pendukung
                                (foto/dokumen) dan diajukan maksimal 3x24 jam
                                setelah barang diterima.
                            </li>
                            <li>
                                Retur yang telah disetujui akan mempengaruhi
                                stok barang secara otomatis (Barang Masuk).
                            </li>
                            <li>
                                Nilai retur akan diperhitungkan dalam
                                penyelesaian pembayaran pelanggan.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesOrderReturnPrint;
